<template>
    <div class="container form-container p-4">
        <div class="text-3xl mb-4 pb-2 font-light flex items-center justify-between">
            <div data-cy="association-settings-companies-title">{{ $t('association.companies.title') }}</div>
            <div>
                <div class="w-auto">
                    <a-checkbox
                        @change="onCheckboxChange"
                        v-model:checked="active_companies_only"
                        data-cy="exclude-inactive-companies"
                    >
                        {{ $t('company.general.inactive_check') }}
                    </a-checkbox>
                </div>
                <div class="text-right">
                    <a-dropdown :trigger="['click']" class="shadow-none" data-cy="company-import-export-dropdown">
                        <a-button style="margin-left: 8px">
                            {{ $t('company.import-export-select.title') }}<DownOutlined />
                        </a-button>
                        <template #overlay>
                            <a-menu @click="onSelectClick">
                                <a-menu-item data-cy="company-import-select" key="Import">
                                    <UploadOutlined />
                                    {{ $t('company.import-export-select.upload') }}
                                </a-menu-item>
                            </a-menu>
                        </template>
                    </a-dropdown>
                </div>
            </div>
        </div>
        <companies-table
            class="w-full"
            ref="CompanyTable"
            :association_id="association_id"
            type="association"
            @onTableAction="onTableAction"
            @onAdd="onAdd"
            @updateDate="updateDate"
        />
        <a-drawer
            data-cy="company-drawer"
            :title="$t('company.general.drawer.title')"
            :width="625"
            :open="drawer_visible"
            :body-style="{ padding: '0px', paddingBottom: '10px' }"
            :destroyOnClose="true"
            :maskClosable="false"
            :rootClassName="'epic-' + moduleStateLink"
            @close="onClose"
        >
            <form-company
                data-cy="company-form"
                :company="company"
                :processing="processing"
                :validation="validation"
                @is-form-dirty="isDrawerFormDirty"
                @formSubmit="onSave"
                @onDrawerClose="onClose"
                ref="CompanyForm"
                form_mode="drawer"
                :is_create="is_create"
                :is_admin_section="false"
                @updateDate="updateDate"
                @updateDeactivationDate="updateDeactivationDate"
                @autofillEmail="autofillEmail"
            >
                <template #actions>
                    <div data-cy="actions-list">
                        <a-dropdown
                            data-cy="actions-select"
                            :trigger="['click']"
                            class="float-left action-select"
                            v-if="company.id"
                        >
                            <a-button @click="(e) => e.preventDefault()">
                                {{ $t('company.general.drawer.views') }}
                                <DownOutlined />
                            </a-button>
                            <template #overlay>
                                <a-menu @click="onActionButtonsForm">
                                    <a-menu-item key="audit_logs" data-cy="actions-audit-logs">
                                        {{ $t('company.general.drawer.action.audit_logs') }}
                                    </a-menu-item>
                                    <a-menu-item
                                        key="upload_history_claims"
                                        data-cy="actions-upload-history-claims"
                                        v-if="isClaimsVaultEnabled"
                                    >
                                        {{ $t('company.general.drawer.action.upload_history_claims') }}
                                    </a-menu-item>
                                    <a-menu-item
                                        key="upload_history_covers"
                                        data-cy="actions-upload-history-covers"
                                        v-if="isCoverVaultEnabled"
                                    >
                                        {{ $t('company.general.drawer.action.upload_history_covers') }}
                                    </a-menu-item>
                                </a-menu>
                            </template>
                        </a-dropdown>
                    </div>
                </template>
            </form-company>
        </a-drawer>
        <modal-upload-logs-claims ref="UploadLogsClaimsModal" :company_id="company.id" />
        <modal-upload-logs-covers ref="UploadLogsCoversModal" :company_id="company.id" />
        <modal-deleted-logs ref="DeletedLogsModal" :company_id="company.id" />
        <modal-audit-logs ref="AuditLogsModal" :company_id="company.id" />
        <modal-import-data
            @formSubmit="onImport"
            :association_id="association_id"
            :processing="processing"
            ref="ImportDataModal"
        />
    </div>
</template>

<script>
import CompaniesTable from '@/components/tables/Companies.vue';
import ModalUploadLogsClaims from '@/components/modals/UploadLogsClaims.vue';
import ModalUploadLogsCovers from '@/components/modals/UploadLogsCovers.vue';
import ModalDeletedLogs from '@/components/modals/DeletedLogs.vue';
import ModalAuditLogs from '@/components/modals/AuditLogs.vue';
import FormCompany from '@/components/forms/Company.vue';
import ModalImportData from '@/components/modals/CompanyImportData.vue';
import UiCheckbox from '@/components/ui/Checkbox.vue';
import { DownOutlined, UploadOutlined } from '@ant-design/icons-vue';

export default {
    components: {
        CompaniesTable,
        ModalUploadLogsClaims,
        ModalUploadLogsCovers,
        ModalDeletedLogs,
        ModalAuditLogs,
        FormCompany,
        ModalImportData,
        UiCheckbox,
        DownOutlined,
        UploadOutlined,
    },
    props: {
        association_name: {
            type: String,
            default: '',
        },
        association_id: {
            type: String,
            default: '',
        },
        association_claimsvault_module: {
            type: Boolean,
            default: false,
        },
        association_covervault_module: {
            type: Boolean,
            default: false,
        },
    },
    data() {
        return {
            company: {},
            drawer_visible: false,
            drawer_form_dirty: false,
            validation: {
                errors: {},
                message: '',
            },
            modal_visible: false,
            processing: false,
            active_companies_only: true,
            is_create: false,
        };
    },
    computed: {
        moduleStateLink() {
            return this.$store.state.system.is_epicvault_enabled ? 'default' : 'claim';
        },
        isClaimsVaultEnabled() {
            return this.$store.state.user.modules.claimsvault;
        },
        isCoverVaultEnabled() {
            return this.$store.state.user.modules.covervault;
        },
    },
    methods: {
        isDrawerFormDirty(is_dirty) {
            this.drawer_form_dirty = is_dirty;
        },
        onAdd() {
            this.openDrawer('');
        },
        openDrawer(id) {
            if (id != '') {
                this.is_create = false;

                this.$http.get('/api/companies/' + id).then((response) => {
                    let company_data = {
                        ...response.data.data,
                        is_active: response.data.data.is_active == 1 ? true : false,
                        is_no_lock_out: response.data.data.is_no_lock_out == 1 ? true : false,
                        is_api_access: response.data.data.is_api_access == 1 ? true : false,
                        is_ip_address_routing: response.data.data.is_ip_address_routing == 1 ? true : false,
                        allow_multi_ip_sessions: response.data.data.allow_multi_ip_sessions == 1 ? true : false,
                        is_replace_tp: response.data.data.is_replace_tp == 1 ? true : false,
                        is_hide_claim_description: response.data.data.is_hide_claim_description == 1 ? true : false,
                        is_multi_association_link: response.data.data.is_multi_association_link == 1 ? true : false,
                        is_data_provider: response.data.data.is_data_provider == 1 ? true : false,
                        is_enforce_filename_restriction:
                            response.data.data.is_enforce_filename_restriction == 1 ? true : false,
                        is_enforce_column_count_restriction:
                            response.data.data.is_enforce_column_count_restriction == 1 ? true : false,
                        update_upload_date_on_new_claims:
                            response.data.data.update_upload_date_on_new_claims == 1 ? true : false,
                        is_module_claimsvault_enabled:
                            response.data.data.is_module_claimsvault_enabled == 1 ? true : false,
                        is_module_covervault_enabled:
                            response.data.data.is_module_covervault_enabled == 1 ? true : false,
                        is_association_module_claimsvault_enabled:
                            response.data.data.is_association_module_claimsvault_enabled == 1 ? true : false,
                        is_association_module_covervault_enabled:
                            response.data.data.is_association_module_covervault_enabled == 1 ? true : false,
                        link_association_ids:
                            response.data.data.link_association_ids === null ||
                            response.data.data.link_association_ids === ''
                                ? []
                                : _.split(response.data.data.link_association_ids, ','),
                        link_company_ids:
                            response.data.data.link_company_ids === null || response.data.data.link_company_ids === ''
                                ? []
                                : _.split(response.data.data.link_company_ids, ','),
                        claim_last_imported_at:
                            response.data.data.claim_last_imported_at === null
                                ? this.$t('company.general.view.never')
                                : this.$dayjs(response.data.data.claim_last_imported_at).format(
                                      'MMMM Do, YYYY [at] hh:mm:ss A',
                                  ),
                        cover_last_imported_at:
                            response.data.data.cover_last_imported_at === null
                                ? this.$t('company.general.view.never')
                                : this.$dayjs(response.data.data.cover_last_imported_at).format(
                                      'MMMM Do, YYYY [at] hh:mm:ss A',
                                  ),
                        deactivation_date:
                            response.data.data.deactivation_date === null
                                ? null
                                : this.$dayjs(response.data.data.deactivation_date).format('MMMM Do, YYYY'),
                        email_address:
                            response.data.data.email_address === null || response.data.data.email_address === ''
                                ? []
                                : _.split(response.data.data.email_address, ','),
                        is_tfa_enabled: response.data.data.is_tfa_enabled === 1 ? true : false,
                        is_association_tfa_enabled: response.data.data.is_association_tfa_enabled === 1 ? true : false,
                        send_import_claim_email: response.data.data.send_import_claim_email === 1 ? true : false,
                        send_import_cover_email: response.data.data.send_import_cover_email === 1 ? true : false,
                        allow_full_association_cover_search:
                            response.data.data.allow_full_association_cover_search === 1 ? true : false,
                        enable_advanced_claims_analytics:
                            response.data.data.enable_advanced_claims_analytics === 1 ? true : false,
                        enable_advanced_cover_analytics:
                            response.data.data.enable_advanced_cover_analytics === 1 ? true : false,
                        email_rejected_records_on_upload_claims:
                            response.data.data.email_rejected_records_on_upload_claims === 1 ? true : false,
                        email_rejected_records_on_upload_covers:
                            response.data.data.email_rejected_records_on_upload_covers === 1 ? true : false,
                        is_send_covers_summary: response.data.data.is_send_covers_summary === 1 ? true : false,
                        is_send_claims_summary: response.data.data.is_send_claims_summary === 1 ? true : false,
                    };

                    this.company = company_data;

                    this.validation = {
                        errors: {},
                        message: '',
                    };
                    this.drawer_visible = true;
                });
            } else {
                this.is_create = true;

                this.company = {
                    association_id: this.association_id,
                    association_name: this.association_name,
                    name: '',
                    company_type: '',
                    company_code: '',
                    contact_first_name: '',
                    contact_last_name: '',
                    mailing_address: '',
                    telephone_number: '',
                    insurer_logo: null,
                    cover_document_filename: null,
                    responsible_user_id: '',
                    email_address: [],
                    salutation: '',
                    ip_range: '',
                    claim_last_imported_at: null,
                    cover_last_imported_at: null,
                    deactivation_date: null,
                    is_active: true,
                    is_no_lock_out: false,
                    is_api_access: false,
                    allow_multi_ip_sessions: false,
                    is_replace_tp: true,
                    is_hide_claim_description: false,
                    is_multi_association_link: false,
                    is_data_provider: false,
                    is_enforce_filename_restriction: false,
                    is_enforce_column_count_restriction: true,
                    update_upload_date_on_new_claims: false,
                    is_module_claimsvault_enabled:
                        this.association_claimsvault_module || this.$store.state.user.modules.claimsvault,
                    is_module_covervault_enabled:
                        this.association_covervault_module || this.$store.state.user.modules.covervault,
                    claim_file_upload_type: 'flat_file',
                    cover_file_upload_type: 'flat_file',
                    is_association_module_claimsvault_enabled:
                        this.association_claimsvault_module || this.$store.state.user.modules.claimsvault,
                    is_association_module_covervault_enabled:
                        this.association_covervault_module || this.$store.state.user.modules.covervault,
                    link_association_ids: [],
                    link_company_ids: [],
                    is_tfa_enabled: false,
                    send_import_claim_email: this.association_claimsvault_module,
                    send_import_cover_email: this.association_covervault_module,
                    is_association_tfa_enabled: false,
                    max_immediate_cover_processing_limit: 0,
                    enable_advanced_claims_analytics: true,
                    enable_advanced_cover_analytics: true,
                    allow_full_association_cover_search: false,
                    email_rejected_records_on_upload_claims: false,
                    email_rejected_records_on_upload_covers: false,
                    is_send_covers_summary: false,
                    is_send_claims_summary: false,
                };

                this.validation = {
                    errors: {},
                    message: '',
                };

                this.drawer_visible = true;
            }
        },
        onTableAction(data) {
            switch (data.key) {
                case 'view':
                    this.openDrawer(data.id);
                    break;
            }
        },
        onActionButtonsForm(event) {
            this.onActionButtons(event, 'form');
        },
        onActionButtons(event, type) {
            this.modal_visible = true;
            switch (event.key) {
                case 'upload_history_claims':
                    this.$refs.UploadLogsClaimsModal.showModal();
                    break;
                case 'upload_history_covers':
                    this.$refs.UploadLogsCoversModal.showModal();
                    break;
                case 'deleted_claims':
                    this.$refs.DeletedLogsModal.showModal();
                    break;
                case 'audit_logs':
                    this.$refs.AuditLogsModal.showModal();
                    break;
            }
        },
        updateDate(id) {
            if (this.processing) {
                return false;
            }

            this.processing = true;

            this.$http
                .post('/api/companies/' + id + '/update-date')
                .then((response) => {
                    if (response.data.status == 'success') {
                        this.$refs.CompanyTable.reload();

                        this.$notification.success({
                            message: response.data.message,
                        });
                    } else {
                        this.$notification.error({
                            message: response.data.message,
                        });
                    }
                })
                .catch((error) => {
                    this.$notification.error({
                        message: this.$t('globals.global.notifications.error.any', { code: 'CVCUID02' }),
                    });
                })
                .finally(() => {
                    this.processing = false;
                });
        },
        updateDeactivationDate(id) {
            if (this.processing) {
                return false;
            }

            this.processing = true;

            this.$http
                .post('/api/companies/' + id + '/update-deactivation-date')
                .then((response) => {
                    if (response.data.status == 'success') {
                        this.$refs.CompanyTable.reload();

                        this.$notification.success({
                            message: response.data.message,
                        });
                    } else {
                        this.$notification.error({
                            message: response.data.message,
                        });
                    }
                })
                .catch((error) => {
                    this.$notification.error({
                        message: this.$t('globals.global.notifications.error.any', { code: 'CVCUDD02' }),
                    });
                })
                .finally(() => {
                    this.processing = false;
                });
        },
        onClose() {
            this.drawer_visible = false;
        },
        onSelectClick(event) {
            switch (event.key) {
                case 'Import':
                    this.$refs.ImportDataModal.showModal();
                    break;
            }
        },
        onImport(data) {
            if (this.processing) {
                return false;
            }

            this.processing = true;
            const headers = { 'Content-Type': 'multipart/form-data' };

            this.$http
                .post('/api/companies/import', data, { headers })
                .then((response) => {
                    if (response.data.status == 'success') {
                        this.$refs.ImportDataModal.fileList = [];
                        this.$refs.ImportDataModal.modal_visible = false;
                        this.$refs.CompanyTable.reload();
                        this.$notification.success({ message: response.data.message });
                    } else {
                        this.$notification.error({ message: response.data.message });
                    }
                })
                .catch((error) => {
                    if (error.response.status == 422) {
                        this.validation = error.response.data;
                    } else if (error.response.status == 403) {
                        this.$notification.error({
                            message: this.$t('globals.global.notifications.error.403_mod'),
                        });
                    } else {
                        this.$notification.error({
                            message: this.$t('globals.global.notifications.error.any', { code: 'CVCUC01' }),
                        });
                    }
                })
                .finally(() => {
                    this.processing = false;
                });
        },
        onCheckboxChange(event) {
            const value = event.target.checked;

            this.active_companies_only = value;
            if (value == true) {
                this.$refs.CompanyTable.fetch({
                    active_companies_only: this.active_companies_only,
                    search_value: this.$refs.CompanyTable.search_value,
                });
            } else {
                this.$refs.CompanyTable.fetch({
                    active_companies_only: this.active_companies_only,
                    search_value: this.$refs.CompanyTable.search_value,
                });
            }
        },
        autofillEmail(fill_data) {
            fill_data.forEach((email) => {
                if (!this.company.email_address.includes(email)) {
                    this.company.email_address.push(email);
                }
            });
        },
        onSave(data) {
            if (this.processing) {
                return false;
            }

            this.drawer_form_dirty = false;
            this.processing = true;

            let id = this.company.id;

            const headers = { 'Content-Type': 'multipart/form-data' };

            if (id == undefined) {
                this.$http
                    .post('/api/companies', data, { headers })
                    .then((response) => {
                        if (response.data.status == 'success') {
                            this.drawer_form_dirty = false;

                            this.$refs.CompanyTable.fetch({
                                results: 10,
                                page: 1,
                                sortField: 'companies.created_at',
                                sortOrder: 'descend',
                            });

                            this.drawer_visible = false;
                        } else {
                            this.$notification.error({
                                message: response.data.message,
                            });
                        }
                    })
                    .catch((error) => {
                        if (error.response.status == 422) {
                            this.validation = error.response.data;
                        } else if (error.response.status == 403) {
                            this.$notification.error({
                                message: this.$t('globals.global.notifications.error.403_mod'),
                            });
                        } else {
                            this.$notification.error({
                                message: this.$t('globals.global.notifications.error.any', { code: 'CVCA02' }),
                            });
                        }
                    })
                    .finally(() => {
                        this.processing = false;
                    });
            } else {
                this.$http
                    .post('/api/companies/' + id + '/partial', data, { headers })
                    .then((response) => {
                        if (response.data.status == 'success') {
                            this.drawer_form_dirty = false;

                            this.$refs.CompanyTable.fetch({
                                results: 10,
                                page: 1,
                                sortField: 'companies.updated_at',
                                sortOrder: 'descend',
                            });

                            this.$refs.CompanyTable.selected_row_keys = [];

                            this.drawer_visible = false;
                        } else {
                            this.$notification.error({
                                message: response.data.message,
                            });
                        }
                    })
                    .catch((error) => {
                        if (error.response.status == 422) {
                            this.validation = error.response.data;
                        } else if (error.response.status == 403) {
                            this.$notification.error({
                                message: this.$t('globals.global.notifications.error.403_mod'),
                            });
                        } else {
                            this.$notification.error({
                                message: this.$t('globals.global.notifications.error.any', { code: 'CVCU02' }),
                            });
                        }
                    })
                    .finally(() => {
                        this.processing = false;
                    });
            }
        },
        reload() {
            this.$refs.CompanyTable.reload();
        },
    },
};
</script>
